/* DCG v2-10-8 · 15 OCT 2026 · DCG.phone.jumpTo(y): a jump the theme makes (the pager landing on the count line) sets the header for where it lands and is not read as the visitor scrolling */
/* DCG v2-9-1 · 10 OCT 2026 · D-SP-28, D-SP-29 · the phone header that shrinks, and return to top */
/* =========================================================
   Desert City Games — phone header and return to top (D-SP-29)
   File: assets/dcg-phone.js   (styles: assets/dcg-phone.css)

   BELOW 768px ONLY. At 768px and up this file sets nothing and changes
   nothing: the header, and the theme's return-to-top link, are as before.

   1. THE HEADER SHRINKS
      The phone header (.dcg-mobile-bar, sections/header-top.liquid) is one
      fixed block: the logo row, the search field, and the Deck Builder /
      Advanced Search buttons, 191px in all. When the customer scrolls down
      it becomes one row of 56px: menu, logo, search, cart. Scrolling up
      brings all of it back. The search button in the slim row brings it
      back too and puts the cursor in the search field.
      The page does not move when the header changes: the block is fixed
      and the space kept for it at the top of the page (--dcg-mobile-bar-h)
      stays the full height. While the phone menu, the filter drawer or the
      search field is in use the header is left as it is.

   2. RETURN TO TOP
      The theme has a return-to-top link already (#slidetop in
      layout/theme.liquid; assets/shop.js shows it after 500px). It is
      reused: here it is shown after about two screens of scrolling and
      hidden again near the top, named "Return to top", and a tap goes to
      the top of the PAGE (not of the results) and brings the full header
      back. It is hidden whenever it would lie over an Add to Cart button,
      the pager, a "My Store Credit" tab, the checkout button or any other
      fixed thing (a banner, a chat button), and while the phone menu or the
      filter drawer is open.

   "Reduce motion": the header changes at once and the page jumps to the
   top with no smooth scroll.
   ========================================================= */
(function () {
  'use strict';

  window.DCG = window.DCG || {};
  if (window.DCG.phone) return;

  const root = document.documentElement;
  const mq = window.matchMedia ? window.matchMedia('(max-width: 767px)') : null;
  const isPhone = () => !!mq && mq.matches;
  const reduceMotion = () => { try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (_) { return false; } };

  const SLIM = 'dcg-hdr-slim';                 // on <html> while the header is the one slim row
  const INSTANT = 'dcg-hdr-instant';           // on <html> for one frame when the header must change with no animation
  const OPENING = 'dcg-hdr-opening';           // on <html> while the header opens again
  const bar = () => document.querySelector('.dcg-mobile-bar');
  const searchButton = () => document.getElementById('dcg-slim-search');
  const searchInput = () => document.querySelector('#store-pass-search-bar-mobile input');
  const menuOpen = () => !!document.body && document.body.classList.contains('dcg-mobile-menu-open');
  const drawerOpen = () => root.classList.contains('dcg-spf-drawer-open');
  const searching = () => { const a = document.activeElement; return !!a && !!a.closest && !!a.closest('#store-pass-search-bar-mobile'); };

  // ═══════════════════════════════════════════════════════════
  // 1. THE HEADER
  // ═══════════════════════════════════════════════════════════
  const SLIM_AFTER = 160;       // px down the page before the header may shrink
  const DOWN = 14;              // px scrolled down, in one go, to shrink it
  const UP = 30;                // px scrolled up, in one go, to bring it back
  const NEAR_TOP = 40;          // px: this near the top it is always whole

  let slim = false;
  let lastY = 0;
  let turnY = 0;                // where the scroll last changed direction
  let goingDown = true;

  function setSlim(on, instant) {
    on = !!on;
    if (slim === on) return;
    slim = on;
    if (instant || reduceMotion()) {
      root.classList.add(INSTANT);
      window.requestAnimationFrame(() => window.requestAnimationFrame(() => root.classList.remove(INSTANT)));
    }
    root.classList.toggle(SLIM, on);
    if (!on) { root.classList.add(OPENING); window.setTimeout(() => root.classList.remove(OPENING), 240); }
    const b = searchButton();
    if (b) b.setAttribute('aria-expanded', on ? 'false' : 'true');
    try { document.dispatchEvent(new CustomEvent('dcg:phone-header', { detail: { slim: on } })); } catch (_) {}
  }

  function headerOnScroll(y) {
    if (!isPhone()) { if (slim) setSlim(false, true); return; }
    if (y > lastY) { if (!goingDown) { goingDown = true; turnY = lastY; } }
    else if (y < lastY) { if (goingDown) { goingDown = false; turnY = lastY; } }
    if (y <= NEAR_TOP) { setSlim(false); return; }
    if (menuOpen() || drawerOpen() || searching()) return;          // in use: left as it is
    if (goingDown && y > SLIM_AFTER && y - turnY >= DOWN) setSlim(true);
    else if (!goingDown && turnY - y >= UP) setSlim(false);
  }

  // The search button of the slim row: the whole header back, the cursor in the search field.
  function openSearch(e) {
    if (e) e.preventDefault();
    setSlim(false);
    turnY = lastY; goingDown = false;
    const focus = () => { const i = searchInput(); if (i) { try { i.focus({ preventScroll: true }); } catch (_) { i.focus(); } } };
    focus();
    window.setTimeout(focus, 220);        // StorePass may still be drawing its field; and after the header has opened
  }

  // ═══════════════════════════════════════════════════════════
  // 2. RETURN TO TOP
  // ═══════════════════════════════════════════════════════════
  const TOP_ID = 'slidetop';
  const TOP_ATTR = 'data-dcg-top';              // "show" | "hide", below 768px; not there at 768px and up
  const NAME = 'Return to top';
  const SHOW_AFTER = 2;                         // screens scrolled before it shows
  const HIDE_BEFORE = 0.75;                     // screens from the top where it hides again
  // what it must never lie over
  const PROTECTED = [
    '.store-pass-product-cta', '.dcg-notify-btn', '.dcg-card-atc', 'button[name="add"]', '.add-to-cart button', '.add_to_cart_main button', '.product-add-to-cart button',
    '[name="checkout"]', '.dcg-pager a', '.dcg-pager__page', '.dcg-pager__step', '.store-pass-pagination-item', '.dcg-view-more', '#binderpos-open-credit'
  ].join(', ');
  const topLink = () => document.getElementById(TOP_ID);
  let wanted = false;           // far enough down
  let oldTitle = null;

  // Is anything it must not cover under it? Its own box, a little larger, sampled at nine points.
  function covered(link) {
    const r = link.getBoundingClientRect();
    if (!r.width || !r.height) return false;
    const pad = 6;
    const xs = [r.left - pad, r.left + r.width / 2, r.right + pad].map(x => Math.max(0, Math.min(window.innerWidth - 1, x)));
    const ys = [r.top - pad, r.top + r.height / 2, r.bottom + pad].map(y => Math.max(0, Math.min(window.innerHeight - 1, y)));
    for (let i = 0; i < xs.length; i++) for (let j = 0; j < ys.length; j++) {
      let stack = [];
      try { stack = document.elementsFromPoint(xs[i], ys[j]) || []; } catch (_) { stack = []; }
      for (let k = 0, seen = 0; k < stack.length && seen < 4; k++) {
        const el = stack[k];
        if (el === link || link.contains(el) || el === document.body || el === root) continue;
        seen++;
        if (el.closest && el.closest(PROTECTED)) return true;
        // any other fixed thing: a banner, a tab, a chat button, an open layer
        for (let p = el, depth = 0; p && p !== document.body && depth < 14; p = p.parentElement, depth++) {
          const pos = window.getComputedStyle(p).position;
          if (pos === 'fixed' || pos === 'sticky') return true;
        }
      }
    }
    return false;
  }

  function topOnScroll(y) {
    const link = topLink();
    if (!link) return;
    if (!isPhone()) {                                   // 768px and up: as the theme had it
      if (link.hasAttribute(TOP_ATTR)) { link.removeAttribute(TOP_ATTR); link.removeAttribute('aria-label'); if (oldTitle !== null) link.setAttribute('title', oldTitle); }
      return;
    }
    if (!link.hasAttribute(TOP_ATTR)) {
      oldTitle = link.getAttribute('title');
      link.setAttribute('aria-label', NAME);
      link.setAttribute('title', NAME);
    }
    const screen = window.innerHeight || 1;
    if (y >= SHOW_AFTER * screen) wanted = true;
    else if (y < HIDE_BEFORE * screen) wanted = false;
    let show = wanted && !menuOpen() && !drawerOpen();
    if (show && covered(link)) show = false;
    const state = show ? 'show' : 'hide';
    if (link.getAttribute(TOP_ATTR) !== state) link.setAttribute(TOP_ATTR, state);
  }

  // A tap: the top of the page, the whole header back. Before shop.js's own handler, which
  // scrolls over 0.8 s whatever the motion setting.
  function onTopClick(e) {
    const link = e.target && e.target.closest ? e.target.closest('#' + TOP_ID) : null;
    if (!link || !isPhone()) return;
    e.preventDefault();
    e.stopPropagation();
    if (e.stopImmediatePropagation) e.stopImmediatePropagation();
    setSlim(false);
    // "instant", not "auto": the theme's stylesheets set scroll-behavior: smooth on the page, and "auto" follows that
    const jump = reduceMotion();
    try { window.scrollTo({ top: 0, left: 0, behavior: jump ? 'instant' : 'smooth' }); } catch (_) { window.scrollTo(0, 0); }
    // After a jump the theme puts the announcement bar back at the top of the page, and the browser
    // holds what was on screen in place by scrolling a few pixels down again (11px seen). The jump
    // is to the very top, so it is made once more when the page has settled.
    if (jump) {
      const again = () => { if ((window.scrollY || 0) > 0) { try { window.scrollTo({ top: 0, left: 0, behavior: 'instant' }); } catch (_) { window.scrollTo(0, 0); } } };
      window.requestAnimationFrame(() => window.requestAnimationFrame(again));
      window.setTimeout(again, 250);
    }
    // a keyboard or a screen reader starts again from the top of the page
    const first = document.querySelector('.dcg-mobile-bar__logo-link, .dcg-mobile-bar__logo-text, #dcg-menu-icon');
    if (first && typeof first.focus === 'function') { try { first.focus({ preventScroll: true }); } catch (_) {} }
  }

  // ═══════════════════════════════════════════════════════════
  // Scroll, resize, and what opens and closes
  // ═══════════════════════════════════════════════════════════
  let ticking = false;
  function tick() {
    ticking = false;
    const y = Math.max(0, window.scrollY || window.pageYOffset || 0);
    try { headerOnScroll(y); } catch (_) {}
    try { topOnScroll(y); } catch (_) {}
    lastY = y;
  }
  function soon() { if (ticking) return; ticking = true; window.requestAnimationFrame(tick); }

  // After the scroll has stopped the button is looked at once more: what is under it has stopped too.
  let rest = null;
  function onScroll() { soon(); if (rest) window.clearTimeout(rest); rest = window.setTimeout(soon, 140); }

  function onResize() {
    // the space kept for the header is measured with the header whole
    if (slim) {
      const was = true;
      setSlim(false, true);
      try { if (typeof window.DCG.measureMobileBar === 'function') window.DCG.measureMobileBar(); } catch (_) {}
      if (was && isPhone() && (window.scrollY || 0) > SLIM_AFTER && !menuOpen() && !drawerOpen() && !searching()) setSlim(true, true);
    }
    soon();
  }

  function start() {
    lastY = turnY = Math.max(0, window.scrollY || 0);
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);
    if (mq) { try { mq.addEventListener('change', onResize); } catch (_) { try { mq.addListener(onResize); } catch (__) {} } }
    document.addEventListener('click', onTopClick, true);
    const b = searchButton();
    if (b) b.addEventListener('click', openSearch);
    // the menu and the filter drawer change classes; the button follows without a scroll
    if (typeof MutationObserver !== 'undefined') {
      const mo = new MutationObserver(soon);
      mo.observe(root, { attributes: true, attributeFilter: ['class'] });
      if (document.body) mo.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    }
    document.addEventListener('focusout', () => window.setTimeout(soon, 60));
    soon();
  }

  // v2-10-8: a jump the theme makes itself (the pager landing on the count line, assets/dcg-storepass-paging.js).
  // The header is set at once for where the page lands (slim past SLIM_AFTER), and the jump is not read as the
  // visitor scrolling: a jump up from the pager would otherwise bring the whole header back over the count line.
  function jumpTo(y) {
    y = Math.max(0, Math.round(y));
    if (isPhone() && !menuOpen() && !drawerOpen() && !searching()) setSlim(y > SLIM_AFTER, true);
    lastY = turnY = y; goingDown = true;
    try { window.scrollTo({ top: y, left: 0, behavior: 'instant' }); } catch (_) { window.scrollTo(0, y); }
  }

  window.DCG.phone = {
    slim: () => slim,
    setSlim: on => setSlim(on),
    jumpTo: y => jumpTo(y),
    openSearch: () => openSearch(null),
    _covered: () => { const l = topLink(); return l ? covered(l) : false; }
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => { try { start(); } catch (_) {} });
  else { try { start(); } catch (_) {} }
})();
